import React, { useEffect, useRef, useState } from 'react';
import { 
  Square, 
  Trash2, 
  RotateCcw, 
  Upload, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  ChevronUp,
  ChevronDown,
  Copy
} from 'lucide-react';
import CVUploadForm from './CVUploadForm';
import { useTranslation } from 'react-i18next';

// Import fabric differently for React/TypeScript
const fabric = require('fabric').fabric;

// Use global pdfjsLib from CDN (loaded in index.html)
// @ts-ignore - pdfjsLib is loaded from CDN
declare const pdfjsLib: any;

interface UploadedFile {
  file: File;
  url: string;
  type: 'pdf' | 'png';
}

interface CVMetadata {
  career_status?: string;
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  is_contactable?: boolean;
}

interface CVEditorProps {
  file: UploadedFile;
  onPagesConverted: (pages: string[]) => void;
  convertedPages: string[];
  currentPageIndex: number;
  onPageChange: (index: number) => void;
  onUpload: (anonymizedImages: string[], consentGiven: boolean) => void;
  isProcessing: boolean;
  uploadStatus: 'idle' | 'uploading' | 'queued' | 'success' | 'error';
  cvMetadata: CVMetadata;
  onMetadataChange: (metadata: CVMetadata) => void;
  profileLoaded: boolean;
  highlightAddBoxButton?: boolean;
  onAddBoxClick?: () => void;
  /** Hide the contact + job-preferences sections (prefs now live on the Passport prefs page). */
  hidePreferences?: boolean;
}

const CVEditor: React.FC<CVEditorProps> = ({
  file,
  onPagesConverted,
  convertedPages,
  currentPageIndex,
  onPageChange,
  onUpload,
  isProcessing,
  uploadStatus,
  cvMetadata,
  onMetadataChange,
  profileLoaded,
  highlightAddBoxButton = false,
  onAddBoxClick,
  hidePreferences = false
}) => {
  const { t } = useTranslation('inspire');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const copiedObjectRef = useRef<fabric.Object | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [anonymizedPages, setAnonymizedPages] = useState<string[]>([]);
  const [canvasStates, setCanvasStates] = useState<string[]>([]);
  
  // GDPR Consent state (combined: anonymization + usage)
  const [consentGiven, setConsentGiven] = useState(false);
  
  // Job Preferences collapsible state
  const [showJobPreferences, setShowJobPreferences] = useState(false);
  
  // Pinch-to-zoom state
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef<{ scaleX: number; scaleY: number } | null>(null);
  const pinchActiveObjectRef = useRef<any>(null);

  // Initialize Fabric.js canvas
  useEffect(() => {
    console.debug('Canvas initialization useEffect, canvasRef:', !!canvasRef.current, 'fabricCanvasRef:', !!fabricCanvasRef.current);
    
    if (canvasRef.current && !fabricCanvasRef.current) {
      console.debug('Initializing Fabric.js canvas');
      try {
        // Mobile-friendly initial size (same 0.8 ratio as desktop for consistency)
        const isMobile = window.innerWidth < 640;
        const initialHeight = isMobile ? 450 : 1000;
        const initialWidth = isMobile ? 360 : 800; // Keep 0.8 ratio like desktop
        
        const canvas = new fabric.Canvas(canvasRef.current, {
          width: initialWidth,
          height: initialHeight,
          backgroundColor: 'white',
          selection: true,
          enablePointerEvents: true,
          allowTouchScrolling: false,
        });

        // Enable touch gestures
        canvas.allowTouchScrolling = false;

        // Add some default settings
        canvas.on('selection:created', () => {
          // Handle selection events if needed
        });

        fabricCanvasRef.current = canvas;
        console.debug('Fabric.js canvas initialized successfully');
      } catch (error) {
        console.error('Error initializing Fabric.js canvas:', error);
      }
    }

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, []);

  // Convert PDF to images when file changes
  useEffect(() => {
    console.debug('File changed:', file);
    
    // Reset canvas states for new file
    setAnonymizedPages([]);
    setCanvasStates([]);
    
    // 🔥 RESET: Completely dispose and reinitialize canvas for new file upload
    // This simulates a fresh first upload instead of trying to update existing canvas
    if (fabricCanvasRef.current) {
      console.debug('🧹 Disposing canvas completely for new file upload');
      fabricCanvasRef.current.dispose();
      fabricCanvasRef.current = null;
      
      // Reinitialize canvas immediately if canvas element exists
      if (canvasRef.current) {
        console.debug('🆕 Reinitializing fresh canvas for new upload');
        try {
          const canvas = new fabric.Canvas(canvasRef.current, {
            width: 800,
            height: 1000,
            backgroundColor: 'white',
            selection: true,
            enablePointerEvents: true,
            allowTouchScrolling: false,
          });
          canvas.allowTouchScrolling = false;
          fabricCanvasRef.current = canvas;
          console.debug('✅ Fresh canvas initialized successfully');
        } catch (error) {
          console.error('❌ Error reinitializing canvas:', error);
        }
      }
    }
    
    // ✅ Don't call onPagesConverted([]) here - let the async conversion handle it
    if (file && file.type === 'pdf') {
      console.debug('Converting PDF to images');
      convertPdfToImages();
    } else if (file && file.type === 'png') {
      console.debug('Loading PNG file directly');
      // For PNG files, just use the file directly
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        console.debug('PNG loaded, data URL length:', result.length);
        onPagesConverted([result]);
      };
      reader.readAsDataURL(file.file);
    }
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load current page into canvas
  useEffect(() => {
    console.debug('convertedPages changed:', convertedPages.length, 'pages', 'currentPageIndex:', currentPageIndex);
    
    // Initialize canvas if not already done
    if (convertedPages.length > 0 && !fabricCanvasRef.current && canvasRef.current) {
      console.debug('Canvas not initialized yet, initializing now...');
      try {
        const canvas = new fabric.Canvas(canvasRef.current, {
          width: 800,
          height: 1000,
          backgroundColor: 'white',
          selection: true,
          enablePointerEvents: true,
          allowTouchScrolling: false,
        });
        canvas.allowTouchScrolling = false;
        fabricCanvasRef.current = canvas;
        console.debug('Canvas initialized on demand');
      } catch (error) {
        console.error('Error initializing canvas on demand:', error);
      }
    }
    
    if (convertedPages.length > 0 && fabricCanvasRef.current && currentPageIndex >= 0 && currentPageIndex < convertedPages.length) {
      console.debug('Loading page', currentPageIndex, 'into canvas');
      loadPageIntoCanvas(convertedPages[currentPageIndex]);
      
      // After loading the page, restore any saved canvas state ONLY if we have states
      if (canvasStates[currentPageIndex]) {
        console.debug('🔄 Found saved state for page', currentPageIndex, '- restoring...');
        setTimeout(() => {
          restoreCanvasState();
        }, 200);
      } else {
        console.debug('✅ No saved state for page', currentPageIndex, '- keeping fresh canvas');
      }
    } else {
      console.debug('Cannot load page - canvas:', !!fabricCanvasRef.current, 'pageIndex:', currentPageIndex, 'valid:', currentPageIndex >= 0 && currentPageIndex < convertedPages.length);
    }
  }, [convertedPages, currentPageIndex]);

  // Restore canvas state for current page
  const restoreCanvasState = () => {
    if (!fabricCanvasRef.current || !canvasStates[currentPageIndex]) {
      console.debug('🔄 No saved state for page', currentPageIndex);
      return;
    }
    
    console.debug('🔄 Restoring canvas state for page', currentPageIndex);
    
    try {
      fabricCanvasRef.current.loadFromJSON(canvasStates[currentPageIndex], () => {
        if (fabricCanvasRef.current) {
          // After restoring, fix properties for all objects
          const objects = fabricCanvasRef.current.getObjects();
          objects.forEach(obj => {
            if (obj.type === 'image') {
              // Make background image unselectable
              obj.set({
                selectable: false,
                evented: false,
                hoverCursor: 'default',
                moveCursor: 'default',
                lockMovementX: true,
                lockMovementY: true,
                lockRotation: true,
                lockScalingX: true,
                lockScalingY: true,
                hasControls: false,
                hasBorders: false,
              });
            } else if (obj.type === 'rect') {
              // Restore corner styling for rectangles (anonymization boxes)
              obj.set({
                cornerColor: '#1a1f2e',
                cornerStrokeColor: '#ffffff',
                cornerStyle: 'circle',
                cornerSize: 8,
                transparentCorners: false,
                borderColor: '#ffffff'
              });
            }
          });
          
          fabricCanvasRef.current.renderAll();
          console.debug('✅ Canvas state restored successfully with protected background and fixed corners');
        }
      });
    } catch (error) {
      console.error('❌ Failed to restore canvas state:', error);
    }
  };

  const convertPdfToImages = async () => {
    if (!file || file.type !== 'pdf') return;

    console.debug('Starting PDF conversion');
    setIsLoading(true);
    try {
      const arrayBuffer = await file.file.arrayBuffer();
      console.debug('PDF arrayBuffer loaded, size:', arrayBuffer.byteLength);
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      console.debug('PDF loaded, pages:', pdf.numPages);
      
      const pages: string[] = [];

      for (let i = 1; i <= Math.min(pdf.numPages, 2); i++) {
        console.debug('Processing page', i);
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2.5 });
        console.debug('Page viewport:', viewport.width, 'x', viewport.height);
        console.debug('PDF scale used:', 2.5);
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        console.debug('Temp canvas size:', canvas.width, 'x', canvas.height);

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const dataUrl = canvas.toDataURL('image/png');
        console.debug('Page', i, 'converted, data URL length:', dataUrl.length);
        console.debug('Data URL format:', dataUrl.substring(0, 30));
        pages.push(dataUrl);
        
        // ✅ Clean up temporary canvas to prevent memory leaks
        canvas.width = 0;
        canvas.height = 0;
        context.clearRect(0, 0, canvas.width, canvas.height);
      }

      console.debug('All pages converted:', pages.length);
      onPagesConverted(pages);
    } catch (error) {
      console.error('Error converting PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPageIntoCanvas = (imageDataUrl: string) => {
    if (!fabricCanvasRef.current) {
      console.debug('No fabric canvas available');
      return;
    }

    console.debug('Loading image into canvas:', imageDataUrl.substring(0, 50) + '...');
    const canvas = fabricCanvasRef.current;
    
    fabric.Image.fromURL(imageDataUrl, (img: any) => {
      console.debug('Image loaded:', img);
      // Clear canvas and set white background
      canvas.clear();
      canvas.setBackgroundColor('white', canvas.renderAll.bind(canvas));
      
      // 🔧 Alternative: Resize canvas to match image aspect ratio
      const imgWidth = img.width || 800;
      const imgHeight = img.height || 1000;
      const aspectRatio = imgWidth / imgHeight;
      
      console.debug('Image aspect ratio:', aspectRatio);
      console.debug('Resizing canvas to better fit image...');
      
      // Resize canvas to maintain aspect ratio while fitting in container
      const isMobile = window.innerWidth < 640;
      const maxWidth = isMobile ? 360 : 800;
      const maxHeight = isMobile ? 450 : 1000;
      let newCanvasWidth, newCanvasHeight;
      
      if (aspectRatio > maxWidth / maxHeight) {
        // Image is wider - fit to width
        newCanvasWidth = maxWidth;
        newCanvasHeight = maxWidth / aspectRatio;
      } else {
        // Image is taller - fit to height
        newCanvasHeight = maxHeight;
        newCanvasWidth = maxHeight * aspectRatio;
      }
      
      console.debug('New canvas size:', newCanvasWidth, 'x', newCanvasHeight);
      canvas.setDimensions({
        width: newCanvasWidth,
        height: newCanvasHeight
      });
      
      // Scale image to fit canvas (use dynamic dimensions)
      const scaleX = newCanvasWidth / imgWidth;
      const scaleY = newCanvasHeight / imgHeight;
      const scale = Math.min(scaleX, scaleY);
      console.debug('Scale calculation: scaleX=', scaleX, 'scaleY=', scaleY, 'chosen scale=', scale);
      
      console.debug('Image dimensions:', img.width, img.height, 'Scale:', scale);
      console.debug('Canvas dimensions:', newCanvasWidth, newCanvasHeight);
      
      // Debug: Check if scale is valid
      if (!scale || scale <= 0) {
        console.error('❌ Invalid scale:', scale);
        return;
      }
      
      img.scale(scale);
      
      const scaledWidth = (img.width || 0) * scale;
      const scaledHeight = (img.height || 0) * scale;
      const leftPos = (newCanvasWidth - scaledWidth) / 2;
      const topPos = (newCanvasHeight - scaledHeight) / 2;
      
      console.debug('Scaled dimensions:', scaledWidth, scaledHeight);
      console.debug('Position:', leftPos, topPos);
      
      img.set({
        left: leftPos,
        top: topPos,
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        moveCursor: 'default',
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        hasControls: false,
        hasBorders: false,
      });
      
      canvas.add(img);
      canvas.sendToBack(img);
      console.debug('Canvas objects after adding image:', canvas.getObjects().length);
      canvas.renderAll();
      console.debug('✅ Image added to canvas and rendered');
    }, {
      crossOrigin: 'anonymous'
    });
  };

  const addAnonymizationBox = () => {
    if (!fabricCanvasRef.current) return;

    const rect = new fabric.Rect({
      left: 100,
      top: 100,
      width: 150,
      height: 30,
      fill: '#1a1f2e',  // Warm dark blue-gray instead of pure black
      stroke: '#ffffff',  // White border - clean and clear
      strokeWidth: 2,  // 2px for better visibility
      rx: 3,  // Slightly rounded corners
      ry: 3,
      cornerColor: '#1a1f2e',  // Black circles to match box fill
      cornerStrokeColor: '#ffffff',  // White stroke for contrast
      cornerStyle: 'circle',
      cornerSize: 8,  // Slightly larger for easier touch
      transparentCorners: false,
      borderColor: '#ffffff',  // White selection border
    });

    fabricCanvasRef.current.add(rect);
    fabricCanvasRef.current.setActiveObject(rect);
    fabricCanvasRef.current.renderAll();
    
    // Dismiss anonymization reminder when user clicks Add Box
    onAddBoxClick?.();
  };

  const deleteSelected = () => {
    if (!fabricCanvasRef.current) return;

    const activeObjects = fabricCanvasRef.current.getActiveObjects();
    activeObjects.forEach(obj => {
      fabricCanvasRef.current!.remove(obj);
    });
    fabricCanvasRef.current.discardActiveObject();
    fabricCanvasRef.current.renderAll();
  };

  const copySelected = () => {
    if (!fabricCanvasRef.current) return;
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (activeObject) {
      copiedObjectRef.current = activeObject;
    }
  };

  const pasteBox = () => {
    if (!copiedObjectRef.current || !fabricCanvasRef.current) return;
    
    copiedObjectRef.current.clone((cloned: fabric.Object) => {
      cloned.set({
        left: (cloned.left || 0) + 20,
        top: (cloned.top || 0) + 20,
        // Ensure consistent corner styling for duplicated boxes
        cornerColor: '#1a1f2e',
        cornerStrokeColor: '#ffffff',
        cornerStyle: 'circle',
        cornerSize: 8,
        transparentCorners: false,
        borderColor: '#ffffff'
      });
      fabricCanvasRef.current!.add(cloned);
      fabricCanvasRef.current!.setActiveObject(cloned);
      fabricCanvasRef.current!.renderAll();
    });
  };

  const duplicateSelected = () => {
    copySelected();
    pasteBox();
  };

  const clearAll = () => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    
    // Remove all objects except the background image
    objects.forEach(obj => {
      if (obj.type !== 'image' || obj.selectable !== false) {
        canvas.remove(obj);
      }
    });
    
    canvas.renderAll();
  };

  const saveCurrentPage = () => {
    if (!fabricCanvasRef.current) return '';

    // Temporarily hide selection controls
    fabricCanvasRef.current.discardActiveObject();
    fabricCanvasRef.current.renderAll();

    const dataUrl = fabricCanvasRef.current.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 2,
    });

    return dataUrl;
  };

  const updateAnonymizedPages = () => {
    if (!fabricCanvasRef.current) return;
    
    const pageData = saveCurrentPage();
    const newAnonymizedPages = [...anonymizedPages];
    newAnonymizedPages[currentPageIndex] = pageData;
    setAnonymizedPages(newAnonymizedPages);
  };

  // Auto-update on any canvas change
  React.useEffect(() => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    const handleCanvasChange = () => {
      // Debounce updates to avoid too many calls
      setTimeout(() => {
        updateAnonymizedPages();
      }, 100);
    };
    
    canvas.on('object:modified', handleCanvasChange);
    canvas.on('object:added', handleCanvasChange);
    canvas.on('object:removed', handleCanvasChange);
    
    // Prevent background image from being selected
    canvas.on('selection:created', (e) => {
      const activeObject = e.selected?.[0];
      if (activeObject && activeObject.type === 'image' && !activeObject.selectable) {
        canvas.discardActiveObject();
        canvas.renderAll();
      }
    });
    
    return () => {
      canvas.off('object:modified', handleCanvasChange);
      canvas.off('object:added', handleCanvasChange);
      canvas.off('object:removed', handleCanvasChange);
      canvas.off('selection:created');
    };
  }, [currentPageIndex, convertedPages]);

  // Keyboard shortcuts: Delete, Copy, Paste
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete/Backspace: Delete selected box
      if ((e.key === 'Delete' || e.key === 'Backspace') && fabricCanvasRef.current) {
        const activeObject = fabricCanvasRef.current.getActiveObject();
        if (activeObject) {
          fabricCanvasRef.current.remove(activeObject);
          fabricCanvasRef.current.renderAll();
        }
      }
      
      // Ctrl+C / Cmd+C: Copy selected box
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && fabricCanvasRef.current) {
        const activeObject = fabricCanvasRef.current.getActiveObject();
        if (activeObject) {
          e.preventDefault();
          copySelected();
        }
      }
      
      // Ctrl+V / Cmd+V: Paste copied box
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && fabricCanvasRef.current) {
        if (copiedObjectRef.current) {
          e.preventDefault();
          pasteBox();
        }
      }
      
      // Ctrl+D / Cmd+D: Duplicate selected box
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && fabricCanvasRef.current) {
        const activeObject = fabricCanvasRef.current.getActiveObject();
        if (activeObject) {
          e.preventDefault();
          duplicateSelected();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Pinch-to-zoom support for mobile and mouse wheel zoom for desktop
  React.useEffect(() => {
    const canvas = fabricCanvasRef.current;
    const container = canvasContainerRef.current;
    
    if (!canvas || !container) return;

    const getTouchDistance = (touches: TouchList): number => {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Mouse wheel zoom (Ctrl + Scroll)
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type !== 'image') {
          e.preventDefault();
          
          const delta = e.deltaY;
          const zoomFactor = delta > 0 ? 0.95 : 1.05;
          
          const currentScaleX = activeObject.scaleX || 1;
          const currentScaleY = activeObject.scaleY || 1;
          
          const newScaleX = currentScaleX * zoomFactor;
          const newScaleY = currentScaleY * zoomFactor;
          
          const minScale = 0.1;
          const maxScale = 10;
          const clampedScaleX = Math.max(minScale, Math.min(maxScale, newScaleX));
          const clampedScaleY = Math.max(minScale, Math.min(maxScale, newScaleY));
          
          activeObject.set({
            scaleX: clampedScaleX,
            scaleY: clampedScaleY
          });
          
          canvas.renderAll();
          canvas.fire('object:modified', { target: activeObject });
        }
      }
    };

    // Touch pinch zoom
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const activeObject = canvas.getActiveObject();
        if (activeObject && activeObject.type !== 'image') {
          e.preventDefault();
          pinchStartDistanceRef.current = getTouchDistance(e.touches);
          pinchStartScaleRef.current = {
            scaleX: activeObject.scaleX || 1,
            scaleY: activeObject.scaleY || 1
          };
          pinchActiveObjectRef.current = activeObject;
          console.debug('Pinch started on object');
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && 
          pinchStartDistanceRef.current && 
          pinchStartScaleRef.current && 
          pinchActiveObjectRef.current) {
        
        e.preventDefault();
        
        const currentDistance = getTouchDistance(e.touches);
        const scaleFactor = currentDistance / pinchStartDistanceRef.current;
        
        const newScaleX = pinchStartScaleRef.current.scaleX * scaleFactor;
        const newScaleY = pinchStartScaleRef.current.scaleY * scaleFactor;
        
        // Apply minimum and maximum scale limits
        const minScale = 0.1;
        const maxScale = 10;
        const clampedScaleX = Math.max(minScale, Math.min(maxScale, newScaleX));
        const clampedScaleY = Math.max(minScale, Math.min(maxScale, newScaleY));
        
        pinchActiveObjectRef.current.set({
          scaleX: clampedScaleX,
          scaleY: clampedScaleY
        });
        
        canvas.renderAll();
        console.debug('Pinching:', scaleFactor.toFixed(2));
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (pinchActiveObjectRef.current && e.touches.length < 2) {
        // Fire the modified event to trigger auto-save
        canvas.fire('object:modified', { target: pinchActiveObjectRef.current });
        console.debug('Pinch ended');
      }
      
      if (e.touches.length < 2) {
        pinchStartDistanceRef.current = null;
        pinchStartScaleRef.current = null;
        pinchActiveObjectRef.current = null;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [convertedPages, currentPageIndex]);

  const handlePageChange = (newIndex: number) => {
    // Save current page first
    if (fabricCanvasRef.current) {
      console.debug('🔄 Saving page', currentPageIndex, 'before switching to', newIndex);
      
      // Save canvas state as JSON
      const canvasState = JSON.stringify(fabricCanvasRef.current.toJSON());
      const newCanvasStates = [...canvasStates];
      newCanvasStates[currentPageIndex] = canvasState;
      setCanvasStates(newCanvasStates);
      
      // Save visual representation
      const pageData = saveCurrentPage();
      const newAnonymizedPages = [...anonymizedPages];
      newAnonymizedPages[currentPageIndex] = pageData;
      setAnonymizedPages(newAnonymizedPages);
      
      console.debug('💾 Saved page data and canvas state');
    }
    
    onPageChange(newIndex);
  };

  const handleUpload = () => {
    // Save current page first
    const currentPageData = saveCurrentPage();
    const finalPages = [...anonymizedPages];
    finalPages[currentPageIndex] = currentPageData;

    // Make sure we have data for all pages
    const pagesToUpload = convertedPages.map((_, index) => 
      finalPages[index] || convertedPages[index]
    );

    setAnonymizedPages(finalPages);
    onUpload(pagesToUpload, consentGiven);
  };



  if (isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-pink-200 border-t-pink-500 mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">{t('editor.converting')}</p>
        </div>
      </div>
    );
  }

  if (convertedPages.length === 0) {
    return (
      <div className="h-96 flex items-center justify-center bg-slate-50 rounded-lg">
        <p className="text-slate-400 text-sm">{t('editor.noPages')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap gap-1 sm:gap-2 p-1.5 sm:p-3 bg-gray-50 rounded-lg">
        <button
          onClick={addAnonymizationBox}
          className={`inline-flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-4 sm:py-2 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-md sm:rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-sm sm:shadow-md text-[11px] sm:text-sm ${
            highlightAddBoxButton 
              ? 'relative z-[10003] animate-pulse ring-4 ring-white shadow-[0_0_30px_rgba(255,255,255,0.8),0_0_60px_rgba(255,165,0,0.5)]' 
              : ''
          }`}
          disabled={isProcessing}
        >
          <Square size={10} className="sm:w-[14px] sm:h-[14px]" />
          {t('editor.addBox')}
        </button>
        
        <button
          onClick={duplicateSelected}
          className="btn-secondary text-[11px] sm:text-sm flex items-center px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg"
          disabled={isProcessing}
        >
          <Copy size={11} className="mr-0.5 sm:mr-1 sm:w-[16px] sm:h-[16px]" />
          {t('editor.duplicate')}
        </button>
        
        <button
          onClick={deleteSelected}
          className="btn-secondary text-[11px] sm:text-sm flex items-center px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg"
          disabled={isProcessing}
        >
          <Trash2 size={11} className="mr-0.5 sm:mr-1 sm:w-[16px] sm:h-[16px]" />
          {t('editor.delete')}
        </button>
        
        <button
          onClick={clearAll}
          className="btn-secondary text-[11px] sm:text-sm flex items-center px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg"
          disabled={isProcessing}
        >
          <RotateCcw size={11} className="mr-0.5 sm:mr-1 sm:w-[16px] sm:h-[16px]" />
          {t('editor.clearAll')}
        </button>

      </div>

      {/* Page Navigation */}
      {convertedPages.length > 1 && (
        <div className="flex items-center justify-between p-1.5 sm:p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <button
            onClick={() => handlePageChange(currentPageIndex - 1)}
            disabled={currentPageIndex === 0 || isProcessing}
            className="btn-secondary text-[11px] sm:text-sm flex items-center disabled:opacity-50 px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg"
          >
            <ChevronLeft size={12} className="mr-0.5 sm:mr-1 sm:w-[16px] sm:h-[16px]" />
            <span className="hidden sm:inline">{t('editor.previous')}</span>
            <span className="sm:hidden">◀</span>
          </button>
          
          <span className="text-[11px] sm:text-sm font-semibold text-slate-600">
            {currentPageIndex + 1}/{convertedPages.length}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPageIndex + 1)}
            disabled={currentPageIndex === convertedPages.length - 1 || isProcessing}
            className="btn-secondary text-[11px] sm:text-sm flex items-center disabled:opacity-50 px-2 py-1 sm:px-4 sm:py-2 rounded-md sm:rounded-lg"
          >
            <span className="hidden sm:inline">{t('editor.next')}</span>
            <span className="sm:hidden">▶</span>
            <ChevronRight size={12} className="ml-0.5 sm:ml-1 sm:w-[16px] sm:h-[16px]" />
          </button>
        </div>
      )}

      {/* Canvas */}
      <div 
        ref={canvasContainerRef}
        className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 max-h-[470px] sm:min-h-[600px] sm:max-h-none flex items-start sm:items-center justify-center py-2 sm:py-4"
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          className="border border-gray-300 sm:!max-h-none w-full sm:w-auto"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </div>

      {/* Allow Career Explorers to Contact Me */}
      {!hidePreferences && (<>
      <div className="bg-gradient-to-br from-yellow-50 to-pink-100 border border-pink-200 rounded-lg p-2.5 sm:p-4 shadow-sm hover:border-pink-300 transition-colors">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <input
            type="checkbox"
            id="is-contactable"
            checked={cvMetadata.is_contactable || false}
            onChange={(e) => onMetadataChange({
              ...cvMetadata,
              is_contactable: e.target.checked
            })}
            disabled={isProcessing}
            className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-pink-500 focus:ring-pink-400 border-gray-300 rounded disabled:opacity-50 flex-shrink-0"
          />
          <span className="text-lg sm:text-xl flex-shrink-0">🫶</span>
          <div className="flex-1 min-w-[180px]">
            <label htmlFor="is-contactable" className="cursor-pointer">
              <div className="text-xs sm:text-sm font-semibold text-gray-900">
                {t('editor.contactableTitle')}
              </div>
              <div className="text-[10px] sm:text-xs text-gray-700 mt-0.5 sm:mt-1">
                {t('editor.contactableSubtitle')}
              </div>
            </label>
          </div>
          {profileLoaded && cvMetadata.is_contactable && (
            <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-[8px] sm:text-xs bg-green-100 text-green-800 rounded-full whitespace-nowrap ml-auto">
              {t('editor.loadedFromProfile')}
            </span>
          )}
        </div>
      </div>

      {/* Wanna Get Found by Recruiters (Collapsible) */}
      <div className="mt-4 sm:mt-6">
        {/* Toggle Header */}
        <button
          onClick={() => setShowJobPreferences(!showJobPreferences)}
          className="w-full p-2.5 sm:p-4 bg-gradient-to-br from-yellow-50 to-pink-100 hover:from-yellow-100 hover:to-pink-200 rounded-lg transition-all duration-200 border border-pink-200 shadow-sm hover:border-pink-300"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 sm:gap-3">
              {showJobPreferences ? (
                <ChevronUp className="text-gray-400 flex-shrink-0 w-3.5 h-3.5 sm:w-5 sm:h-5" />
              ) : (
                <ChevronDown className="text-gray-400 flex-shrink-0 w-3.5 h-3.5 sm:w-5 sm:h-5" />
              )}
              <span className="text-lg sm:text-xl flex-shrink-0">🎯</span>
              <div className="text-left">
                <h2 className="text-xs sm:text-sm font-semibold text-gray-900">
                  {t('editor.recruiterToggle')}
                </h2>
              </div>
            </div>

            <div className="ml-2 hidden sm:block">
              {profileLoaded && (cvMetadata.career_status !== 'invisible' || cvMetadata.base_locations || cvMetadata.preferred_locations || cvMetadata.role_preferences) && (
                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                  {t('editor.loadedFromProfile')}
                </span>
              )}
            </div>
          </div>
          
          {profileLoaded && (cvMetadata.career_status !== 'invisible' || cvMetadata.base_locations || cvMetadata.preferred_locations || cvMetadata.role_preferences) && (
            <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-[8px] sm:text-xs bg-green-100 text-green-800 rounded-full whitespace-nowrap inline-block mt-2 sm:hidden ml-auto">
              {t('editor.loadedFromProfile')}
            </span>
          )}
        </button>

        {/* Collapsible Content */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showJobPreferences ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 pb-4 pt-4 mt-2 bg-white border border-pink-200 border-t-0 rounded-b-lg shadow-sm">
            <CVUploadForm
              onMetadataChange={onMetadataChange}
              isProcessing={isProcessing}
              preloadedData={profileLoaded ? cvMetadata : undefined}
            />
          </div>
        </div>
      </div>
      </>)}

      {/* GDPR Consent Checkboxes */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-300 rounded-xl p-4 sm:p-6 space-y-3 sm:space-y-4 shadow-sm">
        <div className="flex items-center gap-2 mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-gray-200">
          <span className="text-lg sm:text-xl">🤝</span>
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900">{t('editor.consentTitle')}</h3>
        </div>

        <div className="flex items-start space-x-2 sm:space-x-3 p-3 sm:p-4 bg-white rounded-lg border border-gray-200 hover:border-yellow-300 transition-colors">
          <input
            type="checkbox"
            id="consent-full"
            checked={consentGiven}
            onChange={(e) => setConsentGiven(e.target.checked)}
            className="mt-0.5 sm:mt-1 h-3.5 w-3.5 sm:h-5 sm:w-5 text-yellow-500 focus:ring-yellow-400 border-gray-300 rounded cursor-pointer flex-shrink-0"
            disabled={isProcessing}
          />
          <label htmlFor="consent-full" className="text-[10px] sm:text-xs text-gray-700 cursor-pointer leading-relaxed">
            <span className="font-semibold text-gray-900">{t('editor.consentText')}</span> {t('editor.consentRemovedItems')}{' '}
            {t('editor.consentAnd')} <span className="font-semibold text-gray-900">{t('editor.consentWillBe')}</span>
            
            <ul className="mt-2 ml-3 sm:ml-4 space-y-1 list-disc text-[10px] sm:text-xs">
              <li dangerouslySetInnerHTML={{ __html: t('editor.consentBullet1') }} />
              <li dangerouslySetInnerHTML={{ __html: t('editor.consentBullet2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('editor.consentBullet3') }} />
            </ul>
            
            <p className="mt-2 text-[10px] sm:text-xs text-gray-600" dangerouslySetInnerHTML={{ __html: t('editor.consentWarning') }} />
          </label>
        </div>
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={isProcessing || convertedPages.length === 0 || !consentGiven}
        className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-sm sm:text-base"
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white" />
            {t('editor.uploading')}
          </>
        ) : (
          <>
            <Upload size={18} className="sm:w-[20px] sm:h-[20px]" />
            {t('editor.uploadButton')}
          </>
        )}
      </button>
    </div>
  );
};

export default CVEditor;
